import { atsApiQuery } from '..';
import { appendEsignFormData } from '../interview-plan/esignFormData';
import { buildEsignSendBody } from './esignSendOverrides';
import type { EsignSendOverrides } from './esignSendOverrides';
import type {
  EsignActivityPayload,
  EsignDocumentEnvelope,
} from '@/types/esignature';

interface SendArgs {
  atsJobStageActionId: number | string;
  atsApplicationId: number | string;
  /**
   * Per-candidate changes the recruiter made in the send drawer.
   *
   * Omit it and the envelope uses the activity's configuration exactly as
   * today. Anything present here applies to this envelope only — the stage's
   * template, and therefore every other not-yet-sent candidate on the stage, is
   * untouched. Build it with `buildEsignSendOverrides` so only edited keys are
   * included: the backend reads presence, not value.
   */
  overrides?: EsignSendOverrides;
}

interface AdhocArgs {
  atsApplicationId: number | string;
  /**
   * Required, not optional as the original handoff doc had it.
   *
   * An ad-hoc envelope has no stage action to hang off, so without the stage id
   * the backend cannot group it — it comes back under
   * `stage.adhoc_esign_documents[]`.
   */
  atsJobStageId: number | string;
  params: EsignActivityPayload;
}

/**
 * Send and preview for e-sign stage documents.
 *
 * Everything here is asynchronous: all four POSTs return 202 with an envelope id
 * and nothing blocks on DocuSign. The caller keeps that id and polls
 * `getEsignDocumentById` until `external_id_present` flips, then asks for the
 * sender view. `useEsignPreviewFlow` wraps that sequence.
 */
export const esignDocumentsApi = atsApiQuery.injectEndpoints({
  endpoints: (builder) => ({
    // Send the documents configured on a stage activity to one candidate.
    sendEsignDocuments: builder.mutation<EsignDocumentEnvelope, SendArgs>({
      transformResponse: (response: any) => response?.data,
      query: ({ atsJobStageActionId, atsApplicationId, overrides }) => ({
        url: '/esign_documents/send',
        method: 'post',
        data: buildEsignSendBody(
          {
            ats_job_stage_action_id: atsJobStageActionId,
            ats_application_id: atsApplicationId,
          },
          overrides
        ),
      }),
    }),

    // Same, but stops at a DocuSign draft so the recruiter can review and send
    // from DocuSign's embedded sender view.
    previewEsignDocuments: builder.mutation<EsignDocumentEnvelope, SendArgs>({
      transformResponse: (response: any) => response?.data,
      query: ({ atsJobStageActionId, atsApplicationId, overrides }) => ({
        url: '/esign_documents/preview',
        method: 'post',
        data: buildEsignSendBody(
          {
            ats_job_stage_action_id: atsJobStageActionId,
            ats_application_id: atsApplicationId,
          },
          overrides
        ),
      }),
    }),

    // One-off send for a stage with NO configured e-sign activity at all. Not
    // the path for editing a planned activity — that is `send`/`preview` with
    // `overrides`, which keeps the envelope attached to its stage action.
    // An ad-hoc envelope lands under `stage.adhoc_esign_documents[]` instead.
    sendAdhocEsignDocuments: builder.mutation<EsignDocumentEnvelope, AdhocArgs>(
      {
        transformResponse: (response: any) => response?.data,
        query: ({ atsApplicationId, atsJobStageId, params }) => {
          const formData = new FormData();
          formData.append('ats_application_id', String(atsApplicationId));
          formData.append('ats_job_stage_id', String(atsJobStageId));
          return {
            url: '/esign_documents/send_adhoc',
            method: 'post',
            data: appendEsignFormData(formData, params),
          };
        },
      }
    ),

    previewAdhocEsignDocuments: builder.mutation<
      EsignDocumentEnvelope,
      AdhocArgs
    >({
      transformResponse: (response: any) => response?.data,
      query: ({ atsApplicationId, atsJobStageId, params }) => {
        const formData = new FormData();
        formData.append('ats_application_id', String(atsApplicationId));
        formData.append('ats_job_stage_id', String(atsJobStageId));
        return {
          url: '/esign_documents/preview_adhoc',
          method: 'post',
          data: appendEsignFormData(formData, params),
        };
      },
    }),

    // Poll target for both flows.
    getEsignDocumentById: builder.query<
      EsignDocumentEnvelope,
      { id: number | string }
    >({
      transformResponse: (response: any) => response?.data,
      query: ({ id }) => ({ method: 'get', url: `/esign_documents/${id}` }),
    }),

    // 422s with err_esign_preview_not_ready until the draft exists in DocuSign.
    openEsignSenderView: builder.mutation<
      { url?: string },
      { id: number | string }
    >({
      transformResponse: (response: any) => response?.data,
      query: ({ id }) => ({
        method: 'get',
        url: `/esign_documents/${id}/sender_view`,
      }),
    }),
  }),
  overrideExisting: true,
});

export const {
  useSendEsignDocumentsMutation,
  usePreviewEsignDocumentsMutation,
  useSendAdhocEsignDocumentsMutation,
  usePreviewAdhocEsignDocumentsMutation,
  useLazyGetEsignDocumentByIdQuery,
  useOpenEsignSenderViewMutation,
} = esignDocumentsApi;
